import { InternalError } from '../errors/InternalError.js'
import { readPackIndex } from '../storage/readPackIndex.js'
import { join } from '../utils/join.js'
import { shasumRange } from '../utils/shasumRange.js'

export async function readObjectPacked({
  fs,
  cache,
  gitdir,
  oid,
  format = 'content',
  getExternalRefDelta,
}) {
  // Check to see if it's in a packfile.
  // Iterate through all the .idx files
  let list = await fs.readdir(join(gitdir, 'objects/pack'))
  list = list.filter(x => x.endsWith('.idx'))
  for (const filename of list) {
    const indexFile = `${gitdir}/objects/pack/${filename}`
    const p = await readPackIndex({
      fs,
      cache,
      filename: indexFile,
      getExternalRefDelta,
    })
    if (p.error) throw new InternalError(p.error)
    // If the packfile DOES have the oid we're looking for...
    if (p.offsets.has(oid)) {
      // Derive the .pack path from the .idx path
      const packFile = indexFile.replace(/idx$/, 'pack')
      if (!p.pack) {
        const read = fs.read(packFile)
        p.pack = read
        // Let the next call retry a failed read instead of reusing it.
        read.catch(() => {
          if (p.pack === read) p.pack = null
        })
      }
      const pack = await p.pack
      if (!pack) {
        p.pack = null
        throw new InternalError(
          `Could not read packfile at ${packFile}. The file may be missing, corrupted, or too large to read into memory.`
        )
      }

      // === Packfile Integrity Verification ===
      // Performance optimization: verify only once per packfile.
      // Concurrent reads share the same verification promise.
      if (!p._checksumVerification) {
        p._checksumVerification = verifyPackfile(pack, p.packfileSha)
      }
      await p._checksumVerification

      const result = await p.read({ oid, getExternalRefDelta })
      result.format = 'content'
      result.source = `objects/pack/${filename.replace(/idx$/, 'pack')}`
      return result
    }
  }
  // Failed to find it
  return null
}

async function verifyPackfile(pack, expectedShaFromIndex) {
  // 1. Fast Check: Verify packfile trailer matches index record
  // Use subarray instead of slice to avoid memory copy (zero-copy for large packfiles)
  const packTrailer = pack.subarray(-20)
  const packTrailerSha = Array.from(packTrailer)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
  if (packTrailerSha !== expectedShaFromIndex) {
    throw new InternalError(
      `Packfile trailer mismatch: expected ${expectedShaFromIndex}, got ${packTrailerSha}. The packfile may be corrupted.`
    )
  }

  // 2. Deep Integrity Check: Calculate actual SHA-1 of packfile payload.
  // The Node package build swaps in a chunked implementation for large packs.
  const actualPayloadSha = await shasumRange(pack, {
    start: 0,
    end: pack.length - 20,
  })
  if (actualPayloadSha !== expectedShaFromIndex) {
    throw new InternalError(
      `Packfile payload corrupted: calculated ${actualPayloadSha} but expected ${expectedShaFromIndex}. The packfile may have been tampered with.`
    )
  }
}
